import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import FileUpload from '../../components/FileUpload';
import { Plus, Pencil, Trash2, ChevronRight, FolderTree } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCat, setEditCat] = useState(null);
  const [newCat, setNewCat] = useState({ name: '', image: '' });
  const [newSub, setNewSub] = useState({ category_id: '', name: '', image: '' });
  const [showAddCat, setShowAddCat] = useState(false);
  const [showAddSub, setShowAddSub] = useState(false);

  const fetchCategories = () => {
    api.get('/categories').then(r => { setCategories(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchCategories(); }, []);

  const addCategory = async () => {
    if (!newCat.name) { toast.error('Name required'); return; }
    try {
      await api.post('/admin/categories', { name: newCat.name, image: newCat.image, sort_order: categories.length + 1 });
      toast.success('Category added!');
      setNewCat({ name: '', image: '' });
      setShowAddCat(false);
      fetchCategories();
    } catch { toast.error('Failed'); }
  };

  const addSubcategory = async () => {
    if (!newSub.name || !newSub.category_id) { toast.error('Name and category required'); return; }
    try {
      await api.post('/admin/subcategories', newSub);
      toast.success('Subcategory added!');
      setNewSub({ category_id: '', name: '', image: '' });
      setShowAddSub(false);
      fetchCategories();
    } catch { toast.error('Failed'); }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category and all its subcategories?')) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      toast.success('Deleted');
      fetchCategories();
    } catch { toast.error('Failed'); }
  };

  const deleteSubcategory = async (id) => {
    try {
      await api.delete(`/admin/subcategories/${id}`);
      toast.success('Deleted');
      fetchCategories();
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="admin-categories-page" className="p-6 lg:p-8 max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif font-medium">Categories</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage product categories and subcategories</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="add-category-btn" onClick={() => setShowAddCat(true)} className="rounded-full"><Plus className="h-4 w-4 mr-2" /> Category</Button>
          <Button data-testid="add-subcategory-btn" variant="outline" onClick={() => setShowAddSub(true)} className="rounded-full"><Plus className="h-4 w-4 mr-2" /> Subcategory</Button>
        </div>
      </div>

      {/* Add Category Dialog */}
      <Dialog open={showAddCat} onOpenChange={setShowAddCat}>
        <DialogContent className="rounded-xl">
          <DialogHeader><DialogTitle>Add Category</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input data-testid="new-cat-name" value={newCat.name} onChange={e => setNewCat({...newCat, name: e.target.value})} className="mt-1" /></div>
            <div><Label>Image</Label><FileUpload folder="categories" onUpload={url => setNewCat({...newCat, image: url})} /></div>
            <Button data-testid="save-new-cat" onClick={addCategory} className="rounded-full w-full">Add Category</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Subcategory Dialog */}
      <Dialog open={showAddSub} onOpenChange={setShowAddSub}>
        <DialogContent className="rounded-xl">
          <DialogHeader><DialogTitle>Add Subcategory</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Parent Category</Label>
              <select value={newSub.category_id} onChange={e => setNewSub({...newSub, category_id: e.target.value})}
                className="w-full mt-1 h-10 rounded-lg border border-input bg-background px-3 text-sm">
                <option value="">Select category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><Label>Name</Label><Input value={newSub.name} onChange={e => setNewSub({...newSub, name: e.target.value})} className="mt-1" /></div>
            <div><Label>Image</Label><FileUpload folder="categories" onUpload={url => setNewSub({...newSub, image: url})} /></div>
            <Button onClick={addSubcategory} className="rounded-full w-full">Add Subcategory</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Tree */}
      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : (
        <div className="space-y-4">
          {categories.map(cat => (
            <Card key={cat.id} className="rounded-xl">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {cat.image && <img src={cat.image} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                    <div>
                      <h3 className="font-medium">{cat.name}</h3>
                      <p className="text-xs text-muted-foreground">/{cat.slug} &middot; {(cat.subcategories || []).length} subcategories</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={() => deleteCategory(cat.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                {(cat.subcategories || []).length > 0 && (
                  <div className="ml-6 space-y-2 border-l-2 border-border pl-4">
                    {cat.subcategories.map(sub => (
                      <div key={sub.id} className="flex items-center justify-between py-1.5">
                        <div className="flex items-center gap-2">
                          {sub.image && <img src={sub.image} alt="" className="w-8 h-8 rounded object-cover" />}
                          <span className="text-sm">{sub.name}</span>
                          <span className="text-xs text-muted-foreground">/{sub.slug}</span>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => deleteSubcategory(sub.id)} className="h-7 w-7 text-muted-foreground hover:text-destructive"><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
